import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { Lightbox } from 'ngx-lightbox';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import { IPBoardHelper } from 'src/app/helpers/ip-board-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import Asset from 'src/app/model/asset';
import IPBoard from 'src/app/model/product/ip-board/ip-board';
import IPBoardArrows from 'src/app/model/product/ip-board/ip-board-arrows';
import IPBoardPlacement from 'src/app/model/product/ip-board/ip-board-placement';
import { ProjectPlacement } from 'src/app/model/project/projectPlacement';
import { ProjectDisplayObservableService } from 'src/app/services/project-display-observable.service';
import { SubscriptionWithDestroyTriggerComponent } from 'src/app/subscription-helpers/subscription-with-destroy-trigger.component';
import { FileHelper } from '../../../../../../../helpers/file-helper';

@Component({
  selector: 'placement-card-ip-board-extras-info',
  templateUrl: './placement-card-ip-board-extras-info.component.html',
  styleUrls: ['./placement-card-ip-board-extras-info.component.scss'],
})
export class PlacementCardIPBoardExtrasInfoComponent
  extends SubscriptionWithDestroyTriggerComponent
  implements OnInit, OnChanges
{
  @Input()
  ipBoard: IPBoard;

  @Input()
  projectPlacement: ProjectPlacement;

  @Input()
  installerView: boolean = false;

  ipBoardPlacement: IPBoardPlacement;

  anyDisplay: boolean = false;
  applyOtherOrderMargin: boolean = false;

  arrowsAmountDisplay: string;
  arrowsDesignDisplay: string;
  arrowsLocationDisplay: string;
  arrowAssets: Asset[] = [];

  extraHeightDisplay: String;

  secondBoardPlacementDisplay: string;
  secondBoardConfigurationDisplay: string;

  otherOrderDisplay: string;
  isImage = FileHelper.isImage;

  constructor(
    private projectDisplayObservableService: ProjectDisplayObservableService,
    private _lightbox: Lightbox
  ) {
    super();
  }

  ngOnInit(): void {
    this.initializeSubscriptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ipBoard'] != null) {
      this.initializeDisplays();
    }
  }

  private initializeDisplays(): void {
    if (this.ipBoard != null) {
      this.ipBoardPlacement = <IPBoardPlacement>this.ipBoard.productPlacement;
    } else {
      this.ipBoardPlacement = null;
    }

    this.calculateDisplays();
  }

  private initializeSubscriptions(): void {
    const recalculateDisplaysObservable = this.createObservableUntilDestroy(
      this.projectDisplayObservableService.getIPBoardConfigurationChangedSubscribable(),
      this.projectDisplayObservableService.getProductPlacementVariableChangedSubscribable(),
      this.projectDisplayObservableService.getProjectPlacementVariableChangedSubscribable()
    );
    this.addSubscription(
      recalculateDisplaysObservable,
      () => this.calculateDisplays(),
      GeneralHelper.noop,
      500
    );
  }

  private calculateDisplays(): void {
    const extraHeightDisplay = this.calculateExtraHeightDisplay();
    const arrowDisplay = this.calculateArrowsDisplay();
    const secondBoardDisplay = this.calculateSecondBoardDisplay();

    const anyDisplay = extraHeightDisplay || arrowDisplay || secondBoardDisplay;
    const otherOrderDisplay = this.calculateOtherOrderDisplay();

    this.applyOtherOrderMargin = otherOrderDisplay && anyDisplay;
    this.anyDisplay = otherOrderDisplay || anyDisplay;
  }

  private calculateArrowsDisplay(): boolean {
    if (
      !IPBoardHelper.hasArrows(this.ipBoardPlacement) &&
      this.ipBoard['arrows'] == null
    ) {
      this.arrowsAmountDisplay = null;
      this.arrowsDesignDisplay = null;
      this.arrowsLocationDisplay = null;
      return false;
    } else {
      const arrows: IPBoardArrows =
        this.ipBoard['arrows'] != null
          ? this.ipBoard['arrows']
          : this.ipBoardPlacement.arrows;
      const arrowsAmount = StringHelper.join(
        [
          arrows.amountArrowsLeft == null
            ? null
            : `${arrows.amountArrowsLeft} links`,
          arrows.amountArrowsRight == null
            ? null
            : `${arrows.amountArrowsRight} rechts`,
        ],
        ', '
      );
      this.arrowsAmountDisplay = StringHelper.isEmpty(arrowsAmount)
        ? 'Ja'
        : arrowsAmount;
      this.arrowsDesignDisplay = arrows.arrowsDesign;
      this.arrowsLocationDisplay = arrows.arrowsLocation;

      if (this.ipBoard.productAssets && this.ipBoard.productAssets.length > 0) {
        this.arrowAssets = this.ipBoard.productAssets
          .filter((a) => a.formSection === 'arrowsDesign' && a.asset != null)
          .map((a) => a.asset);
      } else {
        // @ts-ignore
        if (this.ipBoard['arrowsAssets'] != null) {
          //client-pending-detail-product.ts
          // @ts-ignore
          this.arrowAssets = this.ipBoard['arrowsAssets'];
        }
      }
      return true;
    }
  }

  private calculateExtraHeightDisplay(): boolean {
    if (
      this.ipBoardPlacement == null ||
      (!this.ipBoardPlacement.extraHeight &&
        (this.ipBoard['extraHeight'] == null ||
          this.ipBoard['extraHeight'] == false))
    ) {
      this.extraHeightDisplay = null;
      return false;
    } else {
      this.extraHeightDisplay =
        'Bord moet op hogere palen (3 meter) geplaatst worden';
      return true;
    }
  }

  private calculateSecondBoardDisplay(): boolean {
    if (
      this.projectPlacement == null ||
      this.projectPlacement.secondBoard == null
    ) {
      this.secondBoardPlacementDisplay = null;
      this.secondBoardConfigurationDisplay = null;
      return false;
    } else {
      this.secondBoardPlacementDisplay = StringHelper.isEmpty(
        this.projectPlacement.secondBoard.secondBoardPlacementInfo
      )
        ? 'Ja'
        : this.projectPlacement.secondBoard.secondBoardPlacementInfo;
      this.secondBoardConfigurationDisplay = this.projectPlacement.secondBoard
        .secondBoardCopy
        ? 'Kopie van origineel besteld bord'
        : this.projectPlacement.secondBoard.secondBoardConfiguration;
      return true;
    }
  }

  private calculateOtherOrderDisplay(): boolean {
    if (
      this.projectPlacement == null ||
      StringHelper.isEmpty(this.projectPlacement.otherOrderPlacementInfo)
    ) {
      this.otherOrderDisplay = null;
      return false;
    } else {
      this.otherOrderDisplay = this.projectPlacement.otherOrderPlacementInfo;
      return true;
    }
  }

  openLightbox(): void {
    let albums = [];
    for (let asset of this.arrowAssets) {
      const src = '/api/assets/' + asset.uuid;
      const caption = asset.name;
      const thumb = '/api/assets/' + asset.uuid;
      const album = {
        src: src,
        caption: caption,
        thumb: thumb,
      };

      albums.push(album);
    }
    this._lightbox.open(albums, 0);
  }
}
